import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { RecruiterEndorsementView } from '@type/endorsements';
import { EndorsementCard } from './EndorsementCard';

interface EndorsementsSectionProps {
  endorsements: RecruiterEndorsementView[];
}

/**
 * Collapsible strip rendered under an experience card on the recruiter-facing
 * CV view. Renders nothing when there are no endorsements — we don't want to
 * add visual noise for the (currently) 100% of CVs without any.
 */
export const EndorsementsSection = ({ endorsements }: EndorsementsSectionProps) => {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState<boolean>(false);

  if (endorsements.length === 0) {
    return null;
  }

  const Toggle = expanded ? ChevronUpIcon : ChevronDownIcon;

  return (
    <section className="mt-2 border-t border-dotted border-surface-100 pt-2">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between text-sm font-semibold text-ink"
      >
        <span>
          {t('endorsement.section.header', 'References')}
          {' '}
          <span className="text-ink-medium">({endorsements.length})</span>
        </span>
        <Toggle className="size-4" />
      </button>

      {expanded && (
        <div className="flex flex-col gap-2">
          {endorsements.map((endorsement) => (
            <EndorsementCard key={endorsement.id} endorsement={endorsement} />
          ))}
        </div>
      )}
    </section>
  );
};
